import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from './store';
import { useResolvedRole, roleHome, type PortalRole } from '@/hooks/useResolvedRole';
import { FullPageLoading } from '@/components/LoadingState';

/**
 * Role-aware route guard. Extends ProtectedRoute's token check with a role
 * check resolved from the backend identity endpoints (see `useResolvedRole`) —
 * never from token shape.
 *
 *   - no token              → /login
 *   - role still resolving  → a lightweight full-screen loading state
 *   - resolution failed     → /forbidden (we can't prove the caller's role,
 *                             so fail closed)
 *   - allow ∉ roles         → the primary role's home (a patient who lands on
 *                             a clinician URL goes to /me; a clinician on /me
 *                             goes to /patients) so a mistyped/stale URL is
 *                             self-correcting rather than a dead end
 *   - allow ∈ roles         → render the nested routes
 *
 * Membership, not equality: a user can hold BOTH a clinician and a patient
 * row, and comparing against one winner locked them out of the other portal
 * entirely. [FOOD-1134]
 *
 * The real security boundary is server-side per-patient authz (Phase-0 P0-C);
 * this guard is UX + defense-in-depth, not the boundary.
 */
export const RoleRoute = ({ allow }: { allow: PortalRole }) => {
  const token = useAuthStore((s) => s.token);
  const { roles, role, isLoading, isError } = useResolvedRole();

  if (!token) return <Navigate to="/login" replace />;
  if (isLoading) return <FullPageLoading />;
  if (isError || !role || roles.length === 0) {
    return <Navigate to="/forbidden" replace />;
  }
  if (!roles.includes(allow)) {
    // `allow` is not in the set, so the primary role is necessarily a
    // different portal — roleHome can't send us back to this route and loop.
    return <Navigate to={roleHome(role)} replace />;
  }
  return <Outlet />;
};
